import React from "react";
import { motion } from "framer-motion";
import { Sparkles, GraduationCap, HeartHandshake, ArrowUpRight } from "lucide-react";

const OFFERS = [
  {
    n: "01",
    tag: "First-time customers",
    title: "10% OFF",
    subtitle: "Your first booking",
    desc: "New to Jaybliss? We take 10% off your first move as a thank-you for choosing us.",
    icon: Sparkles,
  },
  {
    n: "02",
    tag: "Students",
    title: "Student Discount",
    subtitle: "Show your student ID",
    desc: "Moving in, out, or between term-time addresses? Ask us about our student rate on any Man & Van booking.",
    icon: GraduationCap,
  },
  {
    n: "03",
    tag: "Senior citizens",
    title: "Senior Discount",
    subtitle: "Ages 65+",
    desc: "A dedicated discount for senior citizens on all residential removals and single-item deliveries.",
    icon: HeartHandshake,
  },
];

export default function SpecialOffers() {
  return (
    <section className="bg-[var(--jb-navy)] text-[var(--jb-bone)] py-24 md:py-32 relative overflow-hidden" data-testid="offers-section">
      <div className="grain absolute inset-0 pointer-events-none opacity-40" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-20"
        >
          <div className="md:col-span-1 mono-cap text-[var(--jb-rust)]">(Offers)</div>
          <h2 className="md:col-span-8 serif text-[clamp(40px,6vw,88px)] leading-[0.98] tracking-tight">
            Save more with <br />
            <span className="italic text-[var(--jb-rust)]">special offers.</span>
          </h2>
          <p className="md:col-span-3 text-[15px] text-[var(--jb-bone)]/70 leading-[1.6]">
            Discounts you can rely on — no promo codes, no small print. Just mention it when you request a quote.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {OFFERS.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.article
                key={o.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-[var(--jb-bone)]/[0.04] hover:bg-[var(--jb-bone)]/[0.08] border border-[var(--jb-bone)]/15 hover:border-[var(--jb-rust)]/60 p-8 md:p-10 rounded-2xl transition-all duration-500 flex flex-col min-h-[380px]"
                data-testid={`offer-${o.n}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-full bg-[var(--jb-rust)] text-[var(--jb-bone)] grid place-items-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="mono-cap opacity-60">{o.n} / 03</span>
                </div>

                <div className="mono-cap text-[var(--jb-rust)] mb-3">{o.tag}</div>
                <h3 className="serif text-[clamp(36px,3.6vw,52px)] leading-[1] tracking-tight">
                  {o.title}
                </h3>
                <div className="mt-2 text-[15px] text-[var(--jb-bone)]/70">{o.subtitle}</div>

                <p className="mt-6 text-[14px] leading-[1.65] text-[var(--jb-bone)]/70 flex-1">
                  {o.desc}
                </p>

                <a
                  href="#quote"
                  className="mt-8 mono-cap inline-flex items-center gap-2 text-[var(--jb-rust)] link-underline"
                  data-testid={`offer-cta-${o.n}`}
                >
                  Claim this offer <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-10 mono-cap text-[10px] text-[var(--jb-bone)]/45 text-center max-w-[720px] mx-auto">
          Offers subject to availability. Cannot be combined. Mention the offer when requesting your quote and we'll apply it.
        </div>
      </div>
    </section>
  );
}
